/**
 * FirmaKurulumSaatiSekmesi.jsx — Firma Detay › Kurulum Saati (backlog #718, frontend half of backend #717)
 *
 * Bir firmanın bir fuardaki kurulum penceresi: soldaki kutu firmaya ŞU AN bildirilen pencereyi ve her sınırın nereden geldiğini gösterir
 * (FIRMA = firmaya özel pencere, ROL = fuardaki rolleri, FUAR = fuarın kendi Kurulum'u); sağdaki kutu firmaya özel pencereyi yazar / kaldırır.
 * Kural backend'de (bir sınır için: firmanın penceresi, yoksa rollerinin penceresi — en erken başlangıç, en geç bitiş —, yoksa fuarın Kurulum'u);
 * burada yeniden uygulanmaz, yalnızca gösterilir ve kaydedilir. Bu pencere firmanın İSG Taahhütnamesi'nde basılan kurulum aralığıdır.
 *
 * Veri: services/fuarKurulumSaatiService.js → GET / PUT / DELETE /fuar/{fuarId}/firmalar/{firmaId}/kurulum-saati. Yazma page.firma ister;
 * Firma Detay sayfası zaten onu istiyor. PUT ve DELETE taze GET'i döndürür, kutu sonucu hemen gösterir.
 */
import React, { useEffect, useState } from "react";
import { Card, CardBody, Badge, Button, Input, Label, Alert, Spinner, Row, Col, Table } from "reactstrap";
import { useTranslation } from "react-i18next";
import TarihGirdisi from "../../../../Components/Common/TarihGirdisi";
import { tarihSaatMetni } from "../../../../Components/Fairs/fuarAsamalari";
import { fuarService, apiErrorMessage } from "../../../../services/fuarService";
import {
  getFirmaKurulumSaati, saveFirmaKurulumSaati, deleteFirmaKurulumSaati,
  pencereFormu, tarihKisalt, saatKisalt, KURULUM_KAYNAKLARI,
} from "../../../../services/fuarKurulumSaatiService";

const KAYNAK_RENK = { [KURULUM_KAYNAKLARI.FIRMA]: "primary", [KURULUM_KAYNAKLARI.ROL]: "info", [KURULUM_KAYNAKLARI.FUAR]: "secondary" };

const sinirMetni = (tarih, saat) => tarihSaatMetni({ tarih: tarihKisalt(tarih) || null, saat: saatKisalt(saat) || null });
const pencereMetni = (p) => `${sinirMetni(p?.baslangicTarihi, p?.baslangicSaati)} – ${sinirMetni(p?.bitisTarihi, p?.bitisSaati)}`;

export default function FirmaKurulumSaatiSekmesi({ firmaId, fuarId }) {
  const { t, i18n } = useTranslation("moduller");
  const [fuarlar, setFuarlar] = useState([]);
  const [seciliFuar, setSeciliFuar] = useState(Number(fuarId) || null);
  const [veri, setVeri] = useState(null);
  const [form, setForm] = useState(pencereFormu(null));
  const [yukleniyor, setYukleniyor] = useState(true);
  const [hata, setHata] = useState(null);
  const [islemHatasi, setIslemHatasi] = useState(null);
  const [bilgi, setBilgi] = useState(null);
  const [calisiyor, setCalisiyor] = useState(false);

  // KURAL: fetch Effect içindeki async fonksiyonda (react-hooks/set-state-in-effect).
  useEffect(() => {
    let ignore = false;
    async function run() {
      try {
        const liste = await fuarService.getSelectableFuarList();
        if (!ignore) setFuarlar(liste);
      } catch {
        if (!ignore) setFuarlar([]); // the picker then offers only the fair the page was opened with
      }
    }
    run();
    return () => { ignore = true; };
  }, [i18n.language]);

  useEffect(() => {
    if (!seciliFuar) return undefined;
    let ignore = false;
    async function run() {
      setYukleniyor(true);
      setHata(null);
      setIslemHatasi(null);
      setBilgi(null);
      try {
        const data = await getFirmaKurulumSaati(seciliFuar, firmaId);
        if (ignore) return;
        setVeri(data);
        setForm(pencereFormu(data.firmaSaati));
      } catch (e) {
        if (!ignore) { setVeri(null); setHata(apiErrorMessage(e, t("firmaKurulumSaati.okunamadi"))); }
      } finally {
        if (!ignore) setYukleniyor(false);
      }
    }
    run();
    return () => { ignore = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firmaId, seciliFuar]);

  const yaz = async (islem, mesaj) => {
    setCalisiyor(true);
    setIslemHatasi(null);
    setBilgi(null);
    try {
      const data = await islem();
      setVeri(data);
      setForm(pencereFormu(data.firmaSaati));
      setBilgi(mesaj);
    } catch (e) {
      setIslemHatasi(apiErrorMessage(e, t("firmaKurulumSaati.kaydedilemedi")));
    } finally {
      setCalisiyor(false);
    }
  };

  const degistir = (alan, deger) => setForm((f) => ({ ...f, [alan]: deger }));
  const fuarSecenekleri = fuarlar.some((f) => Number(f.fuarId) === seciliFuar) || !seciliFuar
    ? fuarlar
    : [{ fuarId: seciliFuar, fuarName: `#${seciliFuar}` }, ...fuarlar];
  const rolAdi = (rolId) => veri?.roller?.find((r) => r.kurulumRolId === rolId)?.ad || t("firmaKurulumSaati.kaynak.ROL");
  const kaynakRozeti = (kaynak, rolId) => kaynak && (
    <Badge color={KAYNAK_RENK[kaynak] || "light"} className="ms-2" data-testid={`kurulum-kaynak-${kaynak}`}>
      {kaynak === KURULUM_KAYNAKLARI.ROL ? t("firmaKurulumSaati.kaynakRol", { ad: rolAdi(rolId) }) : t(`firmaKurulumSaati.kaynak.${kaynak}`)}
    </Badge>
  );
  const ozelVar = Boolean(veri?.firmaSaati);

  const sinirAlani = (tarihAlani, saatAlani, etiket) => (
    <div>
      <Label className="small mb-1" for={`firma-kurulum-${tarihAlani}`}>{etiket}</Label>
      <div className="d-flex gap-2">
        <TarihGirdisi
          id={`firma-kurulum-${tarihAlani}`} className="flex-grow-1" bsSize={null} yerTutucuMetni={t("firmaKurulumSaati.rollerdenGelir")}
          value={form[tarihAlani]} onChange={(v) => degistir(tarihAlani, v)}
        />
        <Input type="time" aria-label={`${etiket} ${t("firmaKurulumSaati.saat")}`} value={form[saatAlani]} onChange={(e) => degistir(saatAlani, e.target.value)} style={{ maxWidth: 130 }} />
      </div>
    </div>
  );

  return (
    <Card data-testid="firma-kurulum-saati">
      <CardBody>
        <Row className="g-3 align-items-end mb-3">
          <Col md={5} lg={4}>
            <Label className="small mb-1" for="firma-kurulum-fuar">{t("firmaKurulumSaati.fuar")}</Label>
            <Input id="firma-kurulum-fuar" type="select" bsSize="sm" value={seciliFuar || ""} onChange={(e) => setSeciliFuar(Number(e.target.value) || null)}>
              {fuarSecenekleri.map((f) => <option key={f.fuarId} value={f.fuarId}>{f.fuarName || `#${f.fuarId}`}</option>)}
            </Input>
          </Col>
        </Row>

        {hata && <Alert color="danger">{hata}</Alert>}
        {yukleniyor && <div className="text-center py-4"><Spinner color="primary" size="sm" /></div>}

        {!yukleniyor && veri && (
          <Row className="g-4">
            <Col lg={6}>
              <h6 className="mb-3">{t("firmaKurulumSaati.bildirilen")}</h6>
              <div className="mb-2" data-testid="kurulum-baslangic">
                <span className="text-muted small me-2">{t("firmaKurulumSaati.baslangic")}</span>
                <span className="fw-semibold">{sinirMetni(veri.baslangicTarihi, veri.baslangicSaati)}</span>
                {kaynakRozeti(veri.baslangicKaynagi, veri.baslangicRolId)}
              </div>
              <div className="mb-3" data-testid="kurulum-bitis">
                <span className="text-muted small me-2">{t("firmaKurulumSaati.bitis")}</span>
                <span className="fw-semibold">{sinirMetni(veri.bitisTarihi, veri.bitisSaati)}</span>
                {kaynakRozeti(veri.bitisKaynagi, veri.bitisRolId)}
              </div>
              <Table size="sm" className="mb-0 align-middle" data-testid="kurulum-roller">
                <tbody>
                  {(veri.roller || []).map((r) => (
                    <tr key={r.firmaTypeId}>
                      <td className="small">{r.ad}</td>
                      <td className="small text-muted">{r.kurulumRolId ? pencereMetni(r.pencere) : t("firmaKurulumSaati.rolPenceresiYok")}</td>
                    </tr>
                  ))}
                  <tr>
                    <td className="small">{t("firmaKurulumSaati.fuarKurulumu")}</td>
                    <td className="small text-muted">{pencereMetni(veri.varsayilan)}</td>
                  </tr>
                </tbody>
              </Table>
            </Col>

            <Col lg={6}>
              <h6 className="mb-3">{t("firmaKurulumSaati.ozelPencere")}</h6>
              <Row className="g-3">
                <Col md={6}>{sinirAlani("baslangicTarihi", "baslangicSaati", t("firmaKurulumSaati.baslangic"))}</Col>
                <Col md={6}>{sinirAlani("bitisTarihi", "bitisSaati", t("firmaKurulumSaati.bitis"))}</Col>
              </Row>
              {islemHatasi && <Alert color="danger" className="mt-3 mb-0 py-2" data-testid="kurulum-islem-hatasi">{islemHatasi}</Alert>}
              {bilgi && <Alert color="success" className="mt-3 mb-0 py-2">{bilgi}</Alert>}
              <div className="d-flex gap-2 mt-3">
                <Button
                  color="primary" size="sm" disabled={calisiyor} data-testid="kurulum-kaydet"
                  onClick={() => yaz(() => saveFirmaKurulumSaati(seciliFuar, firmaId, form), t("firmaKurulumSaati.kaydedildi"))}
                >
                  {calisiyor ? <Spinner size="sm" className="me-1" /> : <i className="bx bx-save me-1" />}{t("firmaKurulumSaati.kaydet")}
                </Button>
                {ozelVar && (
                  <Button
                    color="danger" outline size="sm" disabled={calisiyor} data-testid="kurulum-kaldir"
                    onClick={() => yaz(() => deleteFirmaKurulumSaati(seciliFuar, firmaId), t("firmaKurulumSaati.kaldirildi"))}
                  >
                    <i className="bx bx-trash me-1" />{t("firmaKurulumSaati.kaldir")}
                  </Button>
                )}
              </div>
            </Col>
          </Row>
        )}
      </CardBody>
    </Card>
  );
}
